/**
 * SanPlumb — Set de iconos SVG (Fase 3)
 *
 * Iconos técnicos propios (no librería externa, §III sin dependencias UI extra).
 * Cada uno es un elemento SVG que hereda `currentColor`, por lo que el color
 * se controla desde CSS (color del contenedor).
 *
 * Portado de `screens.jsx` del diseño aprobado.
 */

import type { ReactElement } from 'react';

type IconEl = ReactElement;

export const Icon: Record<string, IconEl> = {
  upload: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M12 16V4M6 10l6-6 6 6" />
      <path d="M4 20h16" />
    </svg>
  ),
  download: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M12 4v12M6 10l6 6 6-6" />
      <path d="M4 20h16" />
    </svg>
  ),
  recalc: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M4 12a8 8 0 0 1 14-5.3L20 9M20 4v5h-5M20 12a8 8 0 0 1-14 5.3L4 15M4 20v-5h5" />
    </svg>
  ),
  regen: (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
      <path d="M4 12a8 8 0 0 1 14-5.3L20 9M20 4v5h-5" />
      <path d="M20 12a8 8 0 0 1-14 5.3L4 15M4 20v-5h5" />
    </svg>
  ),
  arrow: (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M5 12h14M13 5l7 7-7 7" />
    </svg>
  ),
  chart: (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3">
      <rect x="3" y="13" width="4" height="8" />
      <rect x="10" y="8" width="4" height="13" />
      <rect x="17" y="4" width="4" height="17" />
    </svg>
  ),
  scan: (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3">
      <path d="M3 7V4h3M21 7V4h-3M3 17v3h3M21 17v3h-3" />
      <circle cx="12" cy="12" r="4" />
    </svg>
  ),
  cube: (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3">
      <path d="M12 3 3 7.5v9L12 21l9-4.5v-9z" />
      <path d="M3 7.5 12 12l9-4.5M12 12v9" />
    </svg>
  ),
  doc: (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3">
      <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
      <path d="M14 3v6h6" />
    </svg>
  ),
  plus: (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 5v14M5 12h14" />
    </svg>
  ),
  chev: (
    <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
      <path d="M6 9l6 6 6-6" />
    </svg>
  ),
  check: (
    <svg width="9" height="9" viewBox="0 0 10 10">
      <path
        d="M2 5 L4.3 7.3 L8 3"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  pause: (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
      <rect x="6" y="4" width="4" height="16" rx="0.5" />
      <rect x="14" y="4" width="4" height="16" rx="0.5" />
    </svg>
  ),
  play: (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
      <path d="M6 4l14 8-14 8z" />
    </svg>
  ),
};

/** Iconos del módulo Dotación (portados de extras.jsx → DotIco). */
export const DotIcon: Record<string, IconEl> = {
  building: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="4" y="3" width="16" height="18" />
      <line x1="4" y1="9" x2="20" y2="9" />
      <line x1="4" y1="15" x2="20" y2="15" />
      <line x1="12" y1="3" x2="12" y2="21" />
    </svg>
  ),
  door: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M5 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16" />
      <path d="M3 21h18" />
      <circle cx="15" cy="13" r="0.8" fill="currentColor" />
    </svg>
  ),
  bed: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M3 18v-7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v7" />
      <path d="M3 14h18" />
      <path d="M3 21v-3M21 21v-3" />
      <circle cx="7" cy="11" r="1.4" />
    </svg>
  ),
  heightArr: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M12 4v16" />
      <path d="M8 8l4-4 4 4M8 16l4 4 4-4" />
    </svg>
  ),
  area: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="4" y="4" width="16" height="16" strokeDasharray="2 3" />
      <path d="M4 4l16 16" />
    </svg>
  ),
  drop: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M12 2.5 C 7 9 6 13 6 15 a 6 6 0 0 0 12 0 c 0 -2 -1 -6 -6 -12.5 Z" />
    </svg>
  ),
  cistern: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="4" y="9" width="16" height="11" />
      <path d="M4 13 q 3 -2 6 0 t 6 0 t 4 0" />
      <path d="M12 4v5M9 6h6" />
    </svg>
  ),
  tank: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <ellipse cx="12" cy="5" rx="6" ry="1.6" />
      <path d="M6 5 v13 a 6 1.6 0 0 0 12 0 V 5" />
      <path d="M6 13 q 3 -1.5 6 0 t 6 0" />
    </svg>
  ),
  terma: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="6" y="3" width="12" height="18" rx="1.5" />
      <circle cx="12" cy="9" r="1.6" />
      <path d="M9 17h6" />
    </svg>
  ),
  metric: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M3 17l5-5 4 4 8-9" />
      <path d="M14 7h6v6" />
    </svg>
  ),
};

/** Iconos de aparatos sanitarios (panel Hunter). */
export const AparatoIcon: Record<string, IconEl> = {
  inodoro: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4">
      <rect x="7" y="3" width="10" height="6" rx="0.5" />
      <path d="M5 9h14l-1.5 9a2 2 0 0 1-2 1.6h-7a2 2 0 0 1-2-1.6L5 9z" />
    </svg>
  ),
  lavatorio: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4">
      <path d="M3 9h18v3a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4V9z" />
      <circle cx="12" cy="6" r="1" />
      <path d="M12 4v3" />
    </svg>
  ),
  ducha: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4">
      <path d="M12 2v6M5 11h14l-1 2H6l-1-2z" />
      <path d="M8 16v3M12 16v4M16 16v3" />
    </svg>
  ),
  urinario: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4">
      <path d="M7 3h10v12a4 4 0 0 1-4 4h-2a4 4 0 0 1-4-4V3z" />
      <circle cx="12" cy="8" r="1" />
    </svg>
  ),
  lavadero: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4">
      <rect x="3" y="7" width="18" height="13" rx="1" />
      <path d="M3 11h18" />
      <circle cx="7" cy="15" r="1" />
    </svg>
  ),
  bidet: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4">
      <ellipse cx="12" cy="14" rx="8" ry="5" />
      <path d="M12 3v3M9 6h6" />
    </svg>
  ),
  tina: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4">
      <path d="M3 12h18l-1.5 7a2 2 0 0 1-2 1.5h-11a2 2 0 0 1-2-1.5L3 12z" />
      <circle cx="7" cy="8" r="2" />
    </svg>
  ),
};

/** Devuelve el icono de un aparato por su id (con fallback a lavatorio). */
export function iconForAparato(id: string): IconEl {
  const map: Record<string, IconEl> = {
    inodoro_tanque: AparatoIcon.inodoro,
    inodoro_red: AparatoIcon.inodoro,
    inodoro_valv: AparatoIcon.inodoro,
    lavatorio: AparatoIcon.lavatorio,
    ducha: AparatoIcon.ducha,
    tina: AparatoIcon.tina,
    bidet: AparatoIcon.bidet,
    lavadero: AparatoIcon.lavadero,
    urinario: AparatoIcon.urinario,
  };
  return map[id] ?? AparatoIcon.lavatorio;
}

/** Iconos para las filas del BOM (tuberías + accesorios). */
export const BomIcon: Record<string, IconEl> = {
  pipe: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M2 12h6M16 12h6" />
      <rect x="8" y="9" width="8" height="6" rx="0.5" />
      <path d="M10 9V7M14 9V7" />
    </svg>
  ),
  tee: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M3 10h18v4H3z" />
      <path d="M10 14v7h4v-7" />
    </svg>
  ),
  codo: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M3 21h6a12 12 0 0 0 12-12V3" />
      <path d="M3 17v4h4M17 3h4v4" />
    </svg>
  ),
  niple: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="6" y="10" width="12" height="4" rx="0.5" />
      <path d="M6 9v6M18 9v6" />
    </svg>
  ),
  reduccion: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M3 8h6l6 4H21" />
      <path d="M3 16h6l6-4" />
    </svg>
  ),
  valvula: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M4 12 12 8v8L4 12z" />
      <path d="M20 12l-8 4V8l8 4z" />
      <path d="M12 8V3M9 3h6" />
    </svg>
  ),
  llavePaso: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M3 16h4l2-4h6l2 4h4" />
      <path d="M9 12V8h6v4" />
      <circle cx="12" cy="5" r="1.5" />
      <path d="M10.5 5h3" />
    </svg>
  ),
  unionUniversal: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="12" cy="12" r="6" />
      <path d="M2 12h4M18 12h4M10 12h4" />
    </svg>
  ),
};

/**
 * Devuelve el icono apropiado para una fila del BOM según categoría y label.
 * Heurística: matchea palabras clave del label antes de caer al icono por
 * categoría. Cubre los accesorios típicos del baño peruano (PAVCO/Tigre/Vinilit).
 */
export function iconForBomRow(
  cat: 'tubería' | 'accesorio' | 'válvula' | 'reducción' | 'sanitario',
  label: string,
): IconEl {
  if (cat === 'sanitario') {
    const lc = label.toLowerCase();
    if (lc.startsWith('inodoro')) return AparatoIcon.inodoro;
    if (lc.startsWith('lavatorio')) return AparatoIcon.lavatorio;
    if (lc.includes('tina') || lc.includes('bañera')) return AparatoIcon.tina;
    if (lc.startsWith('ducha')) return AparatoIcon.ducha;
    if (lc.startsWith('urinario')) return AparatoIcon.urinario;
    if (lc.startsWith('lavadero')) return AparatoIcon.lavadero;
    if (lc.startsWith('bidet')) return AparatoIcon.bidet;
    return AparatoIcon.lavatorio;
  }
  if (cat === 'tubería') return BomIcon.pipe;
  if (cat === 'reducción') return BomIcon.reduccion;
  if (cat === 'válvula') return BomIcon.valvula;
  // accesorio: detalle por keyword
  const lc = label.toLowerCase();
  if (lc.includes('llave de paso')) return BomIcon.llavePaso;
  if (lc.includes('tee')) return BomIcon.tee;
  if (lc.includes('codo')) return BomIcon.codo;
  if (lc.includes('unión universal') || lc.includes('union universal')) return BomIcon.unionUniversal;
  if (lc.includes('niple')) return BomIcon.niple;
  if (lc.includes('adaptador')) return BomIcon.niple;
  return BomIcon.tee;
}
